import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { PushPipe } from '@ngrx/component';
import moment from 'moment-timezone';
import type { Observable } from 'rxjs';
import { combineLatest, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';

import type { BillingPlan, PaymentInterval } from '@core/billing-types';
import { BillingPlanTier, Currency } from '@core/billing-types';
import { buildUrlActionTrigger, filterBoolean } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { IntercomRecipients, IntercomService } from '@core/intercom-integration-legacy';
import { SpamVerificationService } from '@core/spam-verification-logic';
import {
  SpamVerificationDialogService,
  SpamVerificationInfoboxContainerComponent,
} from '@core/spam-verification-smart-ui';
import { HeadingComponent } from '@core/user-interface-legacy';
import { BillingPlansDataService, SubscriptionDataService } from '@recruitee/billing-logic';

import { getProductCodeOrThrow } from '../../helpers/get-product-code-or-throw';
import { getProductName } from '../../helpers/get-product-name';
import { ready } from '../../helpers/rx-operators';
import type { PricingFeatureLoadedModel } from '../../types/pricing-feature.types';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../types/pricing-feature.types';
import { AssignedProductMessageContainerComponent } from '../assigned-product-message/assigned-product-message-container.component';
import { planPicker } from './lib/plan-picker';
import { PlanPickerCompareComponent } from './plan-picker-compare/plan-picker-compare.component';
import { PlanPickerGlobalControlsComponent } from './plan-picker-global-controls/plan-picker-global-controls.component';
import { PlanPickerProductsComponent } from './plan-picker-products/plan-picker-products.component';
import { PlanPickerAnalytics } from './plan-picker.analytics';
import type { PlanPickerProduct, PlanPickerState } from './plan-picker.types';
import { ProductStatus } from './plan-picker.types';

@Component({
  selector: 'rt-plan-picker',
  templateUrl: './plan-picker.component.html',
  styleUrls: ['./plan-picker.component.less'],
  providers: [PlanPickerAnalytics],
  imports: [
    HeadingComponent,
    PlanPickerGlobalControlsComponent,
    NgTemplateOutlet,
    SpamVerificationInfoboxContainerComponent,
    InfoboxComponent,
    PlanPickerProductsComponent,
    PlanPickerCompareComponent,
    AssignedProductMessageContainerComponent,
    PushPipe,
    TranslatePipe,
  ],
})
export class PlanPickerComponent {
  private readonly billingPlansData = inject(BillingPlansDataService);
  private readonly subscriptionData = inject(SubscriptionDataService);
  public changeCurrency$ = new Subject<Currency>();
  public changeInterval$ = new Subject<PaymentInterval>();
  public changeSlots$ = new Subject<{ codename: BillingPlanTier; value: number }>();
  public BillingPlanTier = BillingPlanTier;
  public isManualSubscription: boolean = false;
  public state: PlanPickerState;
  public isUpgradingLaunch5$: Observable<boolean>;
  public legacyPlansInfoboxContent = this.getLegacyPlansInfoboxContent();

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    public facade: PricingFeatureFacade,
    public i18n: I18nService,
    public router: Router,
    public analytics: PlanPickerAnalytics,
    protected cdRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private intercomService: IntercomService,
    private featureModel: PricingFeatureObservableModel,
    private spamVerificationService: SpamVerificationService,
    private spamVerificationDialogService: SpamVerificationDialogService,
  ) {
    this.facade.fetchPlansIfNeeded();
  }

  public ngOnInit() {
    const destroy$ = this.ngOnDestroy$.asObservable();
    const featureModel$ = this.featureModel.pipe(ready, shareReplay(1));
    const pickerState$ = planPicker(
      featureModel$,
      this.changeInterval$,
      this.changeCurrency$,
      this.changeSlots$,
      this.billingPlansData.collection$,
      this.billingPlansData.activePlan$,
      this.spamVerificationService.selectSpamVerificationStatus$,
      this.subscriptionData.data$.pipe(
        filterBoolean,
        map(subscription => subscription.addons),
      ),
    ).pipe(shareReplay(1));

    this.isUpgradingLaunch5$ = pickerState$.pipe(
      map(
        state =>
          state.ownedSlots === 5 &&
          state.activeProduct.tier === BillingPlanTier.Launch &&
          state.activeProduct.baseSlots === 5,
      ),
    );

    combineLatest([pickerState$, featureModel$])
      .pipe(takeUntil(destroy$))

      .subscribe(([state, model]) => {
        this.isManualSubscription = !!model.subscription?.manual;
        this.state = state;

        this.setAnalyticsProps(state, model);

        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public continue(product: PlanPickerProduct) {
    if (this.spamVerificationService.verificationRequired()) {
      this.spamVerificationDialogService.open();
      return;
    }

    if (product.status === ProductStatus.Owned) {
      this.navigateToProductPayment(
        product.product!.id,
        product.state.selectedSlots,
        this.state.interval,
      );
    } else {
      this.contactSalesAboutProduct(product.codename);
    }
  }

  public cancelSubscription() {
    if (!this.state.isSelfService) {
      this.contactSalesAboutChurn();
    } else {
      this.router.navigateByUrl(this.facade.getSubscriptionChurnPageUrl().toString());
    }
  }

  public contactSalesAboutChurn() {
    this.intercomService.startNewMessage(
      this.i18n.translate('app.pricing.plan_picker.intercom.churn'),
      IntercomRecipients.Sales,
    );
  }

  public contactSalesAboutProduct(codename: BillingPlanTier) {
    this.intercomService.startNewMessage(
      this.i18n.translate('app.pricing.plan_picker.intercom.interested_in', {
        planName: getProductName(
          {
            codename,
          },
          this.i18n,
          true,
        ),
      }),
      IntercomRecipients.Sales,
    );
  }

  public trackByIndex(index: number) {
    return index;
  }

  public get codename(): BillingPlanTier {
    return this.state.activeProduct.tier;
  }

  public get assignedProductInterval(): PaymentInterval {
    return this.state.assignedProduct!.billingPeriod;
  }

  public openPlanPicker2024() {
    this.facade.openPlanPicker();
  }

  private navigateToProductPayment(
    productId: BillingPlan['id'],
    selectedSlots: number,
    selectedInterval: PaymentInterval,
  ) {
    this.router.navigate([productId, selectedSlots, selectedInterval, 'billing'], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  private setAnalyticsProps(state: PlanPickerState, model: PricingFeatureLoadedModel) {
    this.analytics.setCommonProps({
      assignedProductCode: state.assignedProduct
        ? getProductCodeOrThrow(state.assignedProduct?.tier)
        : undefined,
      slotsLeft: model.trial ? undefined : model.slotsTotal - model.jobSlotsUsed,
      trialDaysLeft: model.trial
        ? moment(model.trial.untilDate).diff(moment(), 'd', false)
        : undefined,
      trial: !!model.trial,
      activePlanCode: getProductCodeOrThrow(state.activeProduct?.tier),
      activePlanCurrency: Currency[state.activeProduct.currency],
      activePlanCustom: state.activeProduct.custom,
      activePlanPaymentInterval: model.subscription?.billingPeriod,
      usedJobSlots: model.jobSlotsUsed,
      selfService: state.isSelfService,
    });
  }

  private getLegacyPlansInfoboxContent() {
    return this.i18n.translate('app.pricing.legacy_plans_phase_out_infobox.content', {
      plan_picker_link: `/#${this.router.serializeUrl(this.facade.getPlanPickerUrl())}`,
      intercom_link: `/#/${buildUrlActionTrigger('IntercomOpen', null, true)}`,
    });
  }
}
